import { useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster, toast } from "sonner";
import { SiteProvider } from "@/context/SiteContext";
import { api } from "@/lib/api";
import Home from "@/pages/Home";

const BlogList = lazy(() => import("@/pages/BlogList"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const WeddingsList = lazy(() => import("@/pages/WeddingsList"));
const WeddingDetail = lazy(() => import("@/pages/WeddingDetail"));
const Partners = lazy(() => import("@/pages/Partners"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const ProfessionalPage = lazy(() => import("@/pages/ProfessionalPage"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + pathname;
    const rmeta = document.querySelector('meta[name="robots"]');
    if (rmeta) rmeta.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1");
  }, [pathname]);
  return null;
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    api
      .post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/admin", { state: { user: data } });
      })
      .catch(() => {
        toast.error("Não foi possível entrar com Google. Tente novamente.");
        navigate("/admin/login");
      });
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen bg-olivedark flex items-center justify-center">
      <p className="text-cream/80 text-sm tracking-[0.2em] uppercase animate-pulse">Entrando com Google...</p>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<div className="min-h-screen bg-cream" />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/dom-frei-lucas-macieira" element={<ProfessionalPage />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/casamentos-realizados" element={<WeddingsList />} />
      <Route path="/casamentos-realizados/:slug" element={<WeddingDetail />} />
      <Route path="/casamentos/:slug" element={<WeddingDetail />} />
      <Route path="/parceiros" element={<Partners />} />
      <Route path="/celebrante-de-casamentos" element={<ServicePage service="celebrante-de-casamentos" />} />
      <Route path="/casamento-religioso" element={<ServicePage service="casamento-religioso" />} />
      <Route path="/casamento-para-divorciados" element={<ServicePage service="casamento-para-divorciados" />} />
      <Route path="/sobre-frei-lucas-macieira" element={<ServicePage service="sobre-frei-lucas-macieira" />} />
      <Route path="/celebrante-de-casamento-belo-horizonte" element={<ServicePage service="celebrante-de-casamento-belo-horizonte" />} />
      <Route path="/celebrante-de-casamento-minas-gerais" element={<ServicePage service="celebrante-de-casamento-minas-gerais" />} />
      <Route path="/cerimonia-de-casamento-personalizada" element={<ServicePage service="cerimonia-de-casamento-personalizada" />} />
      <Route path="/casamento-religioso-com-efeito-civil" element={<ServicePage service="casamento-religioso-com-efeito-civil" />} />
      <Route path="/casamento-em-sitio-chacara-fazenda" element={<ServicePage service="casamento-em-sitio-chacara-fazenda" />} />
      <Route path="/renovacao-de-votos" element={<ServicePage service="renovacao-de-votos" />} />
      <Route path="/celebrante-de-casamento-campo-das-vertentes" element={<ServicePage service="celebrante-de-casamento-campo-das-vertentes" />} />
      <Route path="/celebrante-de-casamentos-minas-gerais" element={<Navigate to="/celebrante-de-casamento-minas-gerais" replace />} />
      <Route path="/celebrante-de-casamentos-bh" element={<Navigate to="/celebrante-de-casamento-belo-horizonte" replace />} />
      <Route path="/celebrante-casamento-belo-horizonte" element={<Navigate to="/celebrante-de-casamento-belo-horizonte" replace />} />
      <Route path="/celebrante-casamento-esmeraldas" element={<CityPage city="esmeraldas" />} />
      <Route path="/celebrante-casamento-contagem" element={<CityPage city="contagem" />} />
      <Route path="/celebrante-casamento-betim" element={<CityPage city="betim" />} />
      <Route path="/celebrante-casamento-nova-lima" element={<CityPage city="nova-lima" />} />
      <Route path="/celebrante-casamento-sete-lagoas" element={<CityPage city="sete-lagoas" />} />
      <Route path="/celebrante-casamento-regiao-metropolitana-bh" element={<CityPage city="regiao-metropolitana-bh" />} />
      <Route path="/politica-de-privacidade" element={<Legal page="politica-de-privacidade" />} />
      <Route path="/politica-de-cookies" element={<Legal page="politica-de-cookies" />} />
      <Route path="/termos-de-uso" element={<Legal page="termos-de-uso" />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/pagamento/sucesso" element={<PaymentResult status="success" />} />
      <Route path="/pagamento/cancelado" element={<PaymentResult status="cancel" />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <LazyMotion features={domAnimation}>
      <SiteProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </SiteProvider>
      </LazyMotion>
    </div>
  );
}

export default App;
